import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarHeader,
	SidebarRail,
} from '@/components/ui/sidebar';
import ESidebarContentCreator from '@/components/everridge/sidebar/components/ESidebarContentCreator';
import { sidebar } from '@/config/sidebar.config';
import { useEffect, useState } from 'react';
import { useUserRole } from '@/features/identity/hooks/useUserRole.ts';
import { usePermissionsListApi } from '@/features/settings/hooks/usePermissionsListApi';
import { SidebarButtons } from '@/components/everridge/sidebar/types';
import { Roles } from '@/@types/auth.types';
import { SidebarHelper } from './utils/sidebarConfig';
import { useLocation } from 'react-router-dom';
import { useMultiCurrencyStorage } from '@/features/hotels/hooks/useHotelMultiCurrencyStorage';

const ESidebar = ({ ...props }: React.ComponentProps<typeof Sidebar>) => {
	const [activeBtn, setActiveBtn] = useState(SidebarButtons.Dashboard);

	const { pathname } = useLocation();
	const userRole = useUserRole();
	const { permissionsListData: permissions } = usePermissionsListApi({
		role: userRole,
	});
	const { ordersEnabled } = useMultiCurrencyStorage();

	useEffect(() => {
		const parts = pathname.slice(1).split('/').map((part) => part.toLowerCase());
		const tabNum = userRole === Roles.superAdmin ? 1 : 0;
		let tabName = parts[tabNum] ?? '';
		if (userRole === Roles.superAdmin && tabName === 'data') {
			tabName = 'data';
		} else if (tabName === 'data' && parts[tabNum + 1]) {
			tabName = parts[tabNum + 1];
		}
		SidebarHelper.setSidebarTab(tabName, setActiveBtn);
	}, [pathname, userRole]);

	if (!userRole) return null;

	const visibleOwnerSidebarElements = SidebarHelper.getFilteredOwners(
		sidebar,
		permissions,
	);
	const visibleAdminSidebarElements = SidebarHelper.getFilteredAdmin(sidebar);

	const filteredSidebar = {
		[Roles.owner]: visibleOwnerSidebarElements,
		[Roles.admin]: visibleAdminSidebarElements,
		[Roles.superAdmin]: sidebar.SuperAdmin,
	};

	const currentSidebar = filteredSidebar[userRole];
	if (!currentSidebar) return null;

	// Re-evaluate enabled() when orders feature flag changes
	void ordersEnabled;

	return (
		<Sidebar
			collapsible='icon'
			{...props}>
			<SidebarHeader className={'p-0'}>
				<ESidebarContentCreator
					data={currentSidebar.header}
					bottomBorder={true}
					type='Header'
				/>
			</SidebarHeader>
			<SidebarContent>
				<ESidebarContentCreator
					data={currentSidebar.main}
					bottomBorder={!!currentSidebar.settings || !!currentSidebar.data}
					activeBtn={activeBtn}
					setActiveBtn={setActiveBtn}
				/>
				<ESidebarContentCreator
					data={currentSidebar.settings}
					activeBtn={activeBtn}
					setActiveBtn={setActiveBtn}
				/>
				<ESidebarContentCreator
					data={currentSidebar.data}
					activeBtn={activeBtn}
					setActiveBtn={setActiveBtn}
				/>
			</SidebarContent>
			<SidebarFooter className={'p-0'}>
				<ESidebarContentCreator
					data={currentSidebar.footer}
					setActiveBtn={setActiveBtn}
					type='Footer'
				/>
			</SidebarFooter>
			<SidebarRail />
		</Sidebar>
	);
};

export default ESidebar;
